import { Card, Table, Select, Button, message, Space, Tag, Popconfirm } from 'antd'
import { SaveOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import axios from 'axios'

// التصنيفات المسموحة — يجب أن تطابق ALLOWED_CATEGORIES بالباك إند
// (incomeStatement.js) حرفياً، لأن إجراء SQL (Stored Procedure) الذي
// يبني قائمة الدخل يعتمد على هذه الرموز الإنجليزية بالضبط
const CATEGORIES = [
  { code: 'Investment', label: 'استثماري' },
  { code: 'Finance', label: 'تمويلي' },
  { code: 'Tax', label: 'ضريبة' },
]

function IncomeStatementSettings() {
  const [mainAccounts, setMainAccounts] = useState([])
  const [classified, setClassified] = useState([]) // [{ GUID, Code, Name, category }]
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [selectedAccGuid, setSelectedAccGuid] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)

  const token = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [accountsRes, classificationsRes] = await Promise.all([
        axios.get('/api/settings/income-statement/main-accounts', { headers }),
        axios.get('/api/settings/income-statement/classifications', { headers })
      ])
      setMainAccounts(accountsRes.data)

      // نبني قائمة العرض بربط كل تصنيف محفوظ باسم/كود الحساب الحيّ
      // الحالي من accountsRes (وليس القيمة القديمة المخزَّنة بـ Options)
      // — هذا يضمن ظهور آخر اسم/كود فعلي حتى لو تغيّر عند العميل لاحقاً
      const accountsByGuid = {}
      accountsRes.data.forEach(acc => { accountsByGuid[acc.GUID] = acc })

      const rows = classificationsRes.data
        .map(row => {
          const acc = accountsByGuid[row.AccGuid]
          return acc ? { GUID: acc.GUID, Code: acc.Code, Name: acc.Name, category: row.CategoryCode } : null
        })
        .filter(Boolean)
      setClassified(rows)
    } catch (err) {
      message.error(err.response?.data?.error || 'خطأ في جلب البيانات')
    }
    setLoading(false)
  }

  // خيارات القائمة المنسدلة: نستثني الحسابات المصنَّفة أصلاً لتفادي
  // إضافة نفس الحساب مرتين بالجدول
  const availableOptions = mainAccounts
    .filter(acc => !classified.some(c => c.GUID === acc.GUID))
    .map(acc => ({ label: `${acc.Code} - ${acc.Name}`, value: acc.GUID }))

  const handleAdd = () => {
    if (!selectedAccGuid || !selectedCategory) {
      message.warning('اختر الحساب والتصنيف أولاً')
      return
    }
    const acc = mainAccounts.find(a => a.GUID === selectedAccGuid)
    if (!acc) return

    setClassified(prev => [...prev, { GUID: acc.GUID, Code: acc.Code, Name: acc.Name, category: selectedCategory }])
    setSelectedAccGuid(null)
    setSelectedCategory(null)
  }

  const handleRemove = (guid) => {
    setClassified(prev => prev.filter(c => c.GUID !== guid))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const accounts = classified.map((c, index) => ({
        accGuid: c.GUID,
        accCode: c.Code,
        accName: c.Name,
        category: c.category,
        sortOrder: index
      }))

      await axios.put(
        '/api/settings/income-statement/classifications',
        { accounts },
        { headers }
      )
      message.success('تم حفظ التصنيف بنجاح ✅')
    } catch (err) {
      message.error(err.response?.data?.error || 'خطأ في الحفظ')
    }
    setSaving(false)
  }

  const categoryLabel = (code) => CATEGORIES.find(c => c.code === code)?.label || code

  const columns = [
    { title: 'كود الحساب', dataIndex: 'Code', key: 'Code', width: 110, render: c => <strong>{c}</strong> },
    { title: 'اسم الحساب', dataIndex: 'Name', key: 'Name' },
    {
      title: 'التصنيف',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: cat => <Tag color="blue">{categoryLabel(cat)}</Tag>
    },
    {
      title: 'إجراء',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Popconfirm title="حذف هذا التصنيف؟" okText="نعم" cancelText="لا" onConfirm={() => handleRemove(record.GUID)}>
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    },
  ]

  return (
    <Card
      title="تصنيف الحسابات الرئيسية — قائمة الدخل"
      loading={loading}
      extra={
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          حفظ التصنيف
        </Button>
      }
    >
      <p style={{ color: '#888', marginBottom: 16 }}>
        اختر الحساب الرئيسي (استثماري / تمويلي / ضريبة فقط) ثم أضفه للجدول أدناه.
        الحسابات غير المُضافة هنا تُعامَل تلقائياً حسب طبيعتها الافتراضية داخل إجراء قائمة الدخل.
      </p>

      <Space size="middle" wrap style={{ marginBottom: 20 }}>
        <Select
          showSearch
          allowClear
          placeholder="ابحث عن حساب بالكود أو الاسم"
          style={{ width: 320 }}
          value={selectedAccGuid}
          onChange={setSelectedAccGuid}
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          options={availableOptions}
        />
        <Select
          placeholder="اختر التصنيف"
          style={{ width: 160 }}
          value={selectedCategory}
          onChange={setSelectedCategory}
          options={CATEGORIES.map(c => ({ label: c.label, value: c.code }))}
        />
        <Button type="dashed" icon={<PlusOutlined />} onClick={handleAdd}>
          إضافة للجدول
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={classified}
        rowKey="GUID"
        pagination={{ pageSize: 20 }}
        size="small"
        locale={{ emptyText: 'لا توجد حسابات مصنَّفة بعد' }}
      />
    </Card>
  )
}

export default IncomeStatementSettings
